import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, User as UserIcon, Trophy, Flame, Award, X, Edit2, Camera, Loader, Image as ImageIcon, MessageSquare, CheckCircle, Lock, TrendingUp, Clock } from 'lucide-react';
import { doc, getDoc, DocumentSnapshot, QuerySnapshot } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { safeOnSnapshot } from './utils/safeFirestore';
import { User, Post } from './types';
import { LevelProgress } from './GamificationComponents';
import { getOrCreateProfile, updateProfile } from './services/profileService';
import { uploadImage, validateMediaFile } from './services/mediaService';
import { getUserPosts } from './services/postService';
import { getOrCreateConversation } from './services/chatService';
import { getPublicProfile, PublicProfile } from './services/publicProfileService';
import { followUser, unfollowUser, isFollowing, isMutualFollow } from './services/followService';
import { isValidAuth } from './utils/authGuard';

export const ProfileView = ({ userId, currentUser, authReady, onBack, onNavigateToChat }: { userId: string, currentUser: User, authReady: boolean, onBack: () => void, onNavigateToChat?: (conversationId: string, otherUserId: string) => void }) => {
  const { t } = useTranslation();
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'permission_denied' | 'unknown' | null>(null);
  const [postsError, setPostsError] = useState(false);
  const [isFollowingUser, setIsFollowingUser] = useState(false);
  const [isMutual, setIsMutual] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [userPosts, setUserPosts] = useState<Post[]>([]);
  const [activeFollowersCount, setActiveFollowersCount] = useState<number>(0); // For Profile Gravity
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subscriptionRef = useRef<(() => void) | null>(null);
  const postsSubscriptionRef = useRef<(() => void) | null>(null);
  
  // CRITICAL: Validate userId and currentUser at component entry - prevent crashes
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    return (
      <div className="p-4 md:p-8 pb-32 space-y-8 max-w-4xl mx-auto animate-fadeIn">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-white mb-4">
          <ArrowLeft size={20} /> {t('profile.back')}
        </button>
        <div className="text-center py-20">
          <p className="text-slate-500">{t('profile.invalid_user_id') || 'ID utente non valido'}</p>
        </div>
      </div>
    );
  }
  
  // CRITICAL: Safe access to currentUser.id - prevent crash if currentUser is undefined
  const isOwnProfile = userId && currentUser?.id ? userId === currentUser.id : false;
  
  // Determine if current user can see private profile information
  const canSeePrivateInfo = isOwnProfile || isMutual;

  // Check follow status and mutual follow
  useEffect(() => {
    if (!currentUser?.id || isOwnProfile) {
      setIsFollowingUser(false);
      setIsMutual(false);
      return;
    }

    const checkFollowStatus = async () => {
      try {
        const [following, mutual] = await Promise.all([
          isFollowing(currentUser.id, userId),
          isMutualFollow(currentUser.id, userId)
        ]);
        
        setIsFollowingUser(following);
        setIsMutual(mutual);
      } catch (error: any) {
        if (import.meta.env.DEV) {
          console.error('[PROFILE] Error checking follow status:', error);
        }
        setIsFollowingUser(false);
        setIsMutual(false);
      }
    };

    checkFollowStatus();
  }, [authReady, currentUser?.id, userId, isOwnProfile]);

  // Load initial profile data (only once on mount or userId change)
  useEffect(() => {
    // AUTH GUARD: Don't run if userId is invalid
    if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
      setError('unknown');
      setLoading(false);
      return;
    }

    // AUTH GUARD: For own profile, require valid auth
    if (isOwnProfile && !isValidAuth(userId)) {
      if (import.meta.env.DEV) {
        console.warn('[PROFILE] Auth guard blocked own profile load - invalid auth');
      }
      setError('unknown');
      setLoading(false);
      return;
    }

    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] Loading profile for userId:', userId, 'isOwnProfile:', isOwnProfile);
    }
    let isMounted = true;
    
    const loadProfile = async () => {
      try {
        if (isOwnProfile) {
          // CRITICAL: Own profile - read from /users
          if (import.meta.env.DEV) {
            console.log('[PROFILE] Loading own profile from /users...');
          }
          const profile = await getOrCreateProfile(userId, {
            email: currentUser?.email || '',
            name: currentUser?.name || '',
            avatar: currentUser?.avatar || ''
          });
          if (isMounted) {
            if (import.meta.env.DEV) {
              console.log('[PROFILE] Own profile loaded:', profile);
            }
            setProfileUser(profile);
            setBioText(profile?.bio || '');
            setError(null);
            setLoading(false);
          }
        } else {
          // CRITICAL: Other user's profile - read from /publicProfiles
          // Public profiles don't require auth, but handle errors gracefully
          if (import.meta.env.DEV) {
            console.log('[PROFILE] Loading other user profile from /publicProfiles...');
          }
          
          let publicProfile: PublicProfile | null = null;
          try {
            publicProfile = await getPublicProfile(userId);
          } catch (profileError: any) {
            // CRITICAL: Handle public profile read failures gracefully
            if (profileError?.code === 'permission-denied') {
              if (import.meta.env.DEV) {
                console.warn('[PROFILE] Permission denied for public profile - showing fallback');
              }
              // Show minimal public fallback UI instead of crashing
              if (isMounted) {
                setError('permission_denied');
                setLoading(false);
              }
              return;
            }
            // For other errors, treat as not found
            if (isMounted) {
              setError('not_found');
              setLoading(false);
            }
            return;
          }
          
          if (!publicProfile) {
            if (isMounted) {
              setError('not_found');
              setLoading(false);
            }
            return;
          }
          
          // Convert PublicProfile to User-like object for UI compatibility
          const profileUser: User = {
            id: publicProfile.userId,
            userId: publicProfile.userId,
            name: publicProfile.name,
            avatar: publicProfile.avatar,
            photoURL: publicProfile.avatar,
            level: publicProfile.level,
            xp: publicProfile.xp,
            title: publicProfile.title,
            badges: publicProfile.badges || [],
            email: '', // Not available in public profile
            bio: '', // Not available in public profile
            friends: [], // Not available in public profile
            role: 'user',
            plan: 'free',
            activeChallenges: [],
            monthlyCalendars: [],
            currentCalendarMonth: undefined,
            calendarConfig: undefined,
            referralStats: {
              totalInvites: 0,
              confirmedSubscriptions: 0,
              currentDiscountPercent: 0,
              totalXPEarned: 0,
              discountsUsed: 0,
              referralBonusUsed: false,
              referralDiscountLocked: false
            },
            referralRewards: {
              earned: 0,
              pending: 0
            },
            public: publicProfile.isPublic,
            createdAt: new Date(),
            updatedAt: publicProfile.updatedAt
          } as User;
          
          if (isMounted) {
            if (import.meta.env.DEV) {
              console.log('[PROFILE] Other user profile loaded:', profileUser);
            }
            setProfileUser(profileUser);
            setBioText(''); // Bio not available for other users
            setError(null);
            setLoading(false);
          }
        }
      } catch (error: any) {
        // CRITICAL: Handle different error types gracefully - never throw
        if (isMounted) {
          try {
            if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
              setError('permission_denied');
            } else if (error?.code === 'not-found' || error?.message?.includes('not found')) {
              setError('not_found');
            } else {
              setError('unknown');
            }
            setLoading(false);
            
            // Real errors should be logged in both dev and prod
            if (import.meta.env.DEV) {
              console.error("[PROFILE] Error loading profile:", {
                code: error?.code,
                message: error?.message
              });
            }
          } catch (errorHandlerError: any) {
            // Even error handling must not throw
            if (import.meta.env.DEV) {
              console.error('[PROFILE] Error in error handler:', errorHandlerError);
            }
            setError('unknown');
            setLoading(false);
          }
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [authReady, userId, isOwnProfile, currentUser?.email, currentUser?.name, currentUser?.avatar]);

  // Subscribe to real-time updates (separate effect to avoid loop)
  // Use ref to track if subscription is already active (idempotent)
  const isSubscribedRef = useRef(false);
  
  useEffect(() => {
    // AUTH GUARD: Don't run if userId is invalid
    if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
      return;
    }

    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // AUTH GUARD: For own profile, require valid auth
    if (isOwnProfile && !isValidAuth(userId)) {
      if (import.meta.env.DEV) {
        console.warn('[PROFILE] Auth guard blocked subscription - invalid auth');
      }
      return;
    }
    
    // Prevent multiple subscriptions (idempotent - safe for StrictMode)
    if (isSubscribedRef.current) {
      // Only log in development
      if (import.meta.env.DEV) {
        console.log('[PROFILE] Subscription already active, skipping');
      }
      return;
    }
    
    // Cleanup previous subscription if exists (safety check)
    // CRITICAL: Guard against calling undefined as a function
    if (subscriptionRef.current && typeof subscriptionRef.current === 'function') {
      // Only log in development
      if (import.meta.env.DEV) {
        console.log('[PROFILE] Cleaning up previous subscription');
      }
      try {
        subscriptionRef.current();
      } catch (cleanupError: any) {
        // Cleanup errors must not crash
        if (import.meta.env.DEV) {
          console.warn('[PROFILE] Error during subscription cleanup:', cleanupError);
        }
      }
      subscriptionRef.current = null;
    }
    
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] Subscribing to real-time updates for userId:', userId, 'isOwnProfile:', isOwnProfile);
    }
    isSubscribedRef.current = true;
    
    // CRITICAL: Use correct collection based on profile type
    // Own profile: /users/{userId}
    // Other profile: /publicProfiles/{userId}
    const userRef = isOwnProfile 
      ? doc(db, "users", userId)
      : doc(db, "publicProfiles", userId);
    
    // CRITICAL FIX: Use getDoc first to check permissions before setting up onSnapshot
    // This prevents "Uncaught Error in snapshot listener" by verifying access first
    // Firestore can throw errors asynchronously that bypass error handlers
    let unsubscribe: (() => void) | null = null;
    
    getDoc(userRef)
      .then((docSnap: DocumentSnapshot) => {
        // Only set up real-time listener if we have permission to read
        try {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            if (userData && typeof userData === 'object') {
              if (isOwnProfile) {
                const safeUserData = userData as User;
                const avatarUrl = safeUserData?.photoURL || safeUserData?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`;
                setProfileUser({ 
                  ...safeUserData,
                  id: docSnap.id,
                  name: safeUserData?.name || 'User',
                  email: safeUserData?.email || '',
                  avatar: avatarUrl,
                  photoURL: avatarUrl
                } as User);
                setBioText(safeUserData?.bio || '');
              } else {
                const publicProfileData = userData as PublicProfile;
                const avatarUrl = publicProfileData?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`;
                const profileUser: User = {
                  id: publicProfileData.userId || docSnap.id,
                  userId: publicProfileData.userId || docSnap.id,
                  name: publicProfileData.name || '',
                  avatar: avatarUrl,
                  photoURL: avatarUrl,
                  level: publicProfileData.level || 1,
                  xp: publicProfileData.xp || 0,
                  title: publicProfileData.title,
                  badges: publicProfileData.badges || [],
                  email: '',
                  bio: '',
                  friends: [],
                  role: 'user',
                  plan: 'free',
                  activeChallenges: [],
                  monthlyCalendars: [],
                  currentCalendarMonth: undefined,
                  calendarConfig: undefined,
                  referralStats: {
                    totalInvites: 0,
                    confirmedSubscriptions: 0,
                    currentDiscountPercent: 0,
                    totalXPEarned: 0,
                    discountsUsed: 0,
                    referralBonusUsed: false,
                    referralDiscountLocked: false
                  },
                  referralRewards: {
                    earned: 0,
                    pending: 0
                  },
                  public: publicProfileData.isPublic,
                  createdAt: new Date(),
                  updatedAt: publicProfileData.updatedAt || new Date()
                } as User;
                setProfileUser(profileUser);
                setBioText('');
              }
              setError(null);
            } else {
              setError('not_found');
            }
          } else {
            setError('not_found');
          }
          setLoading(false);
          
          // 🔒 REGOLA D'ORO: onSnapshot SOLO sul proprio profilo
          // Altri profili → solo getDoc() one-shot (già fatto sopra)
          if (!isOwnProfile) {
            // Non è il proprio profilo → niente realtime
            return;
          }
          
          // Now set up real-time listener ONLY for own profile (we know we have permission)
          try {
            const snapshotResult = safeOnSnapshot(userRef, (snap: DocumentSnapshot | QuerySnapshot) => {
              try {
                // Type guard: check if it's a DocumentSnapshot (not QuerySnapshot)
                if ('exists' in snap && snap.exists()) {
                  const docSnap = snap as DocumentSnapshot;
                  const userData = docSnap.data();
                  
                  // CRITICAL: Validate userData exists and is an object
                  if (!userData || typeof userData !== 'object') {
                    if (import.meta.env.DEV) {
                      console.warn('[PROFILE] Invalid userData received from snapshot');
                    }
                    return;
                  }
                  
                  if (isOwnProfile) {
                    // CRITICAL: Own profile - data comes from /users (User type)
                    const safeUserData = userData as User;
                    const avatarUrl = safeUserData?.photoURL || safeUserData?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`;
                    
                    setProfileUser(prev => {
                      const prevAvatar = prev?.photoURL || prev?.avatar;
                      const hasAvatarChanged = prevAvatar !== avatarUrl;
                      
                      if (prev && !hasAvatarChanged && prev.bio === safeUserData?.bio && prev.name === safeUserData?.name) {
                        return prev;
                      }
                      
                      const updatedUser: User = { 
                        ...safeUserData,
                        id: docSnap.id,
                        name: safeUserData?.name || prev?.name || 'User',
                        email: safeUserData?.email || prev?.email || '',
                        avatar: avatarUrl,
                        photoURL: avatarUrl,
                        level: safeUserData?.level || prev?.level || 1,
                        xp: safeUserData?.xp || prev?.xp || 0,
                        badges: safeUserData?.badges || prev?.badges || [],
                        bio: safeUserData?.bio || prev?.bio || ''
                      } as User;
                      
                      return updatedUser;
                    });
                  } else {
                    // CRITICAL: Other user's profile - data comes from /publicProfiles (PublicProfile type)
                    const publicProfileData = userData as PublicProfile;
                    const avatarUrl = publicProfileData?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`;
                    
                    // Convert PublicProfile to User for UI compatibility
                    const updatedUser: User = {
                      id: publicProfileData.userId || docSnap.id,
                      userId: publicProfileData.userId || docSnap.id,
                      name: publicProfileData.name || '',
                      avatar: avatarUrl,
                      photoURL: avatarUrl,
                      level: publicProfileData.level || 1,
                      xp: publicProfileData.xp || 0,
                      title: publicProfileData.title,
                      badges: publicProfileData.badges || [],
                      email: '',
                      bio: '',
                      friends: [],
                      role: 'user',
                      plan: 'free',
                      activeChallenges: [],
                      monthlyCalendars: [],
                      currentCalendarMonth: undefined,
                      calendarConfig: undefined,
                      referralStats: {
                        totalInvites: 0,
                        confirmedSubscriptions: 0,
                        currentDiscountPercent: 0,
                        totalXPEarned: 0,
                        discountsUsed: 0,
                        referralBonusUsed: false,
                        referralDiscountLocked: false
                      },
                      referralRewards: {
                        earned: 0,
                        pending: 0
                      },
                      public: publicProfileData.isPublic,
                      createdAt: new Date(),
                      updatedAt: publicProfileData.updatedAt || new Date()
                    } as User;
                    
                    setProfileUser(updatedUser);
                    setBioText('');
                  }
                }
              } catch (snapshotError: any) {
                // CRITICAL: Handle errors inside snapshot callback
                if (import.meta.env.DEV) {
                  console.error('[PROFILE] Error processing snapshot data:', snapshotError);
                }
              }
            }, (error: any) => {
              // CRITICAL: Handle errors gracefully
              try {
                if (import.meta.env.DEV && error?.code !== 'permission-denied') {
                  console.error("[PROFILE] Error in real-time subscription:", error);
                }
              } catch {
                // Silently ignore
              }
              return;
            });
            
            // CRITICAL: Verify snapshotResult is a function before storing
            if (typeof snapshotResult === 'function') {
              unsubscribe = snapshotResult;
              subscriptionRef.current = unsubscribe;
            } else {
              if (import.meta.env.DEV) {
                console.error('[PROFILE] onSnapshot did not return a valid unsubscribe function');
              }
              subscriptionRef.current = null;
            }
          } catch (snapshotInitError: any) {
            // Handle errors during onSnapshot initialization
            if (import.meta.env.DEV) {
              console.error('[PROFILE] Error initializing snapshot listener:', snapshotInitError);
            }
            subscriptionRef.current = null;
          }
        } catch (setStateError: any) {
          // Handle setState errors
          if (import.meta.env.DEV) {
            console.error('[PROFILE] Error setting profile state:', setStateError);
          }
          try {
            setError('unknown');
            setLoading(false);
          } catch {
            // Silently ignore
          }
        }
      })
      .catch((error: any) => {
        // CRITICAL: Handle permission-denied and other errors from getDoc
        // This prevents "Uncaught Error" by catching it here
        if (error?.code === 'permission-denied') {
          if (import.meta.env.DEV) {
            console.warn('[PROFILE] Permission denied accessing profile - showing fallback UI');
          }
          setError('permission_denied');
        } else if (error?.code === 'not-found') {
          setError('not_found');
        } else {
          if (import.meta.env.DEV) {
            console.error('[PROFILE] Error loading profile:', error);
          }
          setError('unknown');
        }
        try {
          setLoading(false);
        } catch {
          // Silently ignore
        }
        subscriptionRef.current = null;
      });
    
    return () => {
      // Only log in development
      if (import.meta.env.DEV) {
        console.log('[PROFILE] Unsubscribing from profile updates');
      }
      isSubscribedRef.current = false; // Reset flag
      // CRITICAL: Guard against calling undefined as a function
      if (subscriptionRef.current && typeof subscriptionRef.current === 'function') {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
    };
  }, [authReady, userId]); // Only re-subscribe if userId changes

  // Load user posts (only depends on userId, not profileUser to avoid loop)
  // Use ref to track if subscription is already active (idempotent)
  const isPostsSubscribedRef = useRef(false);
  
  useEffect(() => {
    // AUTH GUARD: Don't run if userId is invalid
    if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
      return;
    }
    
    // Prevent multiple subscriptions (idempotent - safe for StrictMode)
    if (isPostsSubscribedRef.current) {
      // Only log in development
      if (import.meta.env.DEV) {
        console.log('[PROFILE] Posts subscription already active, skipping');
      }
      return;
    }
    
    // Cleanup previous subscription if exists (safety check)
    // CRITICAL: Guard against calling undefined as a function
    if (postsSubscriptionRef.current && typeof postsSubscriptionRef.current === 'function') {
      // Only log in development
      if (import.meta.env.DEV) {
        console.log('[PROFILE] Cleaning up previous posts subscription');
      }
      try {
        postsSubscriptionRef.current();
      } catch (cleanupError: any) {
        // Cleanup errors must not crash
        if (import.meta.env.DEV) {
          console.warn('[PROFILE] Error during posts subscription cleanup:', cleanupError);
        }
      }
      postsSubscriptionRef.current = null;
    }
    
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] Loading posts for user:', userId);
    }
    isPostsSubscribedRef.current = true;
    
    // 🔒 CRITICAL FIX: Use one-shot getDocs() instead of onSnapshot for posts
    // Posts of other users - no realtime needed
    const loadPosts = async () => {
      try {
        const posts = await getUserPosts(userId, 20);
        // Only log in development
        if (import.meta.env.DEV) {
          console.log('[PROFILE] User posts loaded:', posts.length);
        }
        setUserPosts(posts || []);
        setPostsError(false); // Clear error on success
      } catch (postsError: any) {
        // Handle errors in getUserPosts
        if (import.meta.env.DEV) {
          console.error('[PROFILE] Error loading user posts:', postsError);
        }
        setPostsError(true);
        setUserPosts([]);
      }
    };
    
    loadPosts();
    
    return () => {
      // No cleanup needed for one-shot fetch
      isPostsSubscribedRef.current = false;
      postsSubscriptionRef.current = null;
    };
  }, [authReady, userId]); // Only re-subscribe if userId changes

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      // Only log in development - production console should be clean
      if (import.meta.env.DEV) {
        console.log('[AVATAR] No file selected');
      }
      return;
    }

    // CRITICAL: Verify file is a valid File instance
    if (!(file instanceof File)) {
      // Real errors should be logged in both dev and prod
      console.error('[AVATAR] Invalid file object:', file);
      alert(t('profile.file_invalid'));
      return;
    }

    // CRITICAL: Verify Firebase Auth has current user (required for Storage auth token)
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || !firebaseUser.uid) {
      // Real errors should be logged in both dev and prod
      console.error('[AVATAR] Firebase Auth currentUser is null or missing uid');
      alert(t('profile.auth_required'));
      return;
    }

    // Verify user is authenticated (from props)
    if (!currentUser || !currentUser.id) {
      console.error('[AVATAR] User not authenticated (from props)');
      alert(t('profile.auth_required_simple'));
      return;
    }

    // CRITICAL: Verify userId matches Firebase Auth uid (security check)
    if (userId !== firebaseUser.uid) {
      console.error('[AVATAR] User ID mismatch - userId:', userId, 'vs auth.currentUser.uid:', firebaseUser.uid);
      alert(t('profile.permission_denied'));
      return;
    }

    // Verify userId matches currentUser (additional security check)
    if (userId !== currentUser.id) {
      console.error('[AVATAR] User ID mismatch - userId:', userId, 'vs currentUser.id:', currentUser.id);
      alert(t('profile.permission_denied'));
      return;
    }

    console.log('[AVATAR] ===== UPLOAD START =====');
    console.log('[AVATAR] File selected:', file.name, file.size, 'bytes', file.type);
    console.log('[AVATAR] Current user ID:', currentUser.id);
    console.log('[AVATAR] Profile user ID:', userId);
    
    const validation = validateMediaFile(file, 'image');
    if (!validation.valid) {
      console.error('[AVATAR] Validation failed:', validation.error);
      alert(validation.error || t('profile.file_invalid'));
      return;
    }

    // Prevent multiple simultaneous uploads
    if (uploadingAvatar) {
      console.warn('[AVATAR] Upload già in corso, ignorando richiesta');
      return;
    }

    setUploadingAvatar(true);
    let previewUrl: string | null = null;
    
    try {
      // Verify file is valid
      if (!file || file.size === 0) {
        throw new Error(t('profile.file_invalid_empty'));
      }

      // Create preview
      previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
      console.log('[AVATAR] Preview created, uploading to Storage...');

      // Upload to Firebase Storage
      console.log('[AVATAR] Calling uploadImage...');
      const imageUrl = await uploadImage(file, userId, 'avatar');
      console.log('[AVATAR] ✅ Uploaded to Storage:', imageUrl);
      
      // CRITICAL: Update Firebase Auth photoURL first
      console.log('[AVATAR] Updating Firebase Auth photoURL...');
      try {
        const { updateProfile: updateAuthProfile } = await import('firebase/auth');
        if (firebaseUser) {
          await updateAuthProfile(firebaseUser, { photoURL: imageUrl });
          console.log('[AVATAR] ✅ Firebase Auth photoURL updated successfully');
        }
      } catch (authError: any) {
        console.error('[AVATAR] ⚠️ Error updating Firebase Auth photoURL (non-blocking):', authError);
        // Continue with Firestore update even if Auth update fails
      }
      
      // Update profile in Firestore
      console.log('[AVATAR] Updating profile with new avatar URL...');
      try {
        // CRITICAL: Save as both 'avatar' (for backward compatibility) and 'photoURL' (as required)
        // This ensures the avatar persists and is accessible via both field names
        await updateProfile(userId, { 
          avatar: imageUrl,
          photoURL: imageUrl  // Save as photoURL as required
        });
        console.log('[AVATAR] ✅ Profile updated successfully in Firestore with photoURL:', imageUrl);
        
        // CRITICAL: Update local state immediately for reactive UI update
        // The real-time listener will also update, but this ensures instant feedback
        if (profileUser) {
          setProfileUser({ 
            ...profileUser, 
            avatar: imageUrl,
            photoURL: imageUrl  // Update both fields in local state
          });
        }
      } catch (profileError: any) {
        console.error('[AVATAR] ❌ Error updating profile:', profileError);
        // Even if profile update fails, the image is uploaded - log but don't fail completely
        throw new Error(t('profile.avatar_upload_error', { message: profileError.message }));
      }
      
      // Cleanup preview
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setAvatarPreview(null);
      alert(t('profile.photo_updated'));
      console.log('[AVATAR] ===== UPLOAD SUCCESS =====');
    } catch (error: any) {
      console.error('[AVATAR] ===== UPLOAD ERROR =====');
      console.error('[AVATAR] Error type:', error.constructor.name);
      console.error('[AVATAR] Error code:', error.code);
      console.error('[AVATAR] Error message:', error.message);
      console.error('[AVATAR] Error stack:', error.stack);
      
      // Cleanup preview on error
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setAvatarPreview(null);
      
      // Show user-friendly error
      const errorMessage = error.message || t('profile.photo_error');
      alert(errorMessage);
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-32 space-y-8 max-w-4xl mx-auto animate-fadeIn">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-white mb-4">
          <ArrowLeft size={20} /> {t('profile.back')}
        </button>
        <div className="text-center py-20">
          <Loader className="animate-spin text-cyan-500 w-12 h-12 mx-auto mb-4" />
          <p className="text-slate-500">{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  // Show error states gracefully - never crash, always show meaningful UI
  if (error === 'permission_denied') {
    // CRITICAL: Show minimal public fallback UI instead of crashing
    // This allows the profile to still be viewable with limited info
    return (
      <div className="p-4 md:p-8 pb-32 space-y-8 max-w-4xl mx-auto animate-fadeIn">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-white mb-4">
          <ArrowLeft size={20} /> {t('profile.back')}
        </button>
        
        {/* Minimal Public Profile Fallback */}
        <div className="bg-slate-900 border border-slate-800 rounded-[3rem] p-8 text-center space-y-4">
          <div className="w-24 h-24 rounded-full bg-slate-800 mx-auto flex items-center justify-center">
            <UserIcon className="text-slate-600" size={48} />
          </div>
          <h2 className="text-xl font-black text-white">Profilo privato</h2>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            Questo profilo è privato. Segui l'utente per vedere di più.
          </p>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="p-4 md:p-8 pb-32 space-y-8 max-w-4xl mx-auto animate-fadeIn">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-white mb-4">
          <ArrowLeft size={20} /> {t('profile.back')}
        </button>
        <div className="text-center py-20 space-y-4">
          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-2xl inline-block">
            <UserIcon className="text-slate-500 mx-auto mb-2" size={48} />
          </div>
          <h2 className="text-xl font-black text-white">{t('profile.user_not_found') || 'Profilo non trovato'}</h2>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            {t('profile.user_not_found_message') || 'Il profilo che stai cercando non esiste o è stato rimosso.'}
          </p>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    // CRITICAL: Fallback for unknown errors - always show meaningful UI, never crash
    // This should rarely happen, but if it does, show a helpful message
    return (
      <div className="p-4 md:p-8 pb-32 space-y-8 max-w-4xl mx-auto animate-fadeIn">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-white mb-4">
          <ArrowLeft size={20} /> {t('profile.back')}
        </button>
        <div className="text-center py-20 space-y-4">
          <UserIcon className="text-slate-600 mx-auto" size={64} />
          <h2 className="text-xl font-black text-slate-300">Profilo non disponibile</h2>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Impossibile caricare i dati del profilo. Riprova più tardi.
          </p>
        </div>
      </div>
    );
  }

  // Extract calendar logic before JSX to avoid inline IIFE
  // Calculate current month calendar for stamps display
  // CRITICAL: Safe defaults for all data access
  const now = new Date();
  const monthlyCalendars = profileUser?.monthlyCalendars || [];
  const currentCalendar = monthlyCalendars.find(
    cal => cal?.year === now.getFullYear() && cal?.month === now.getMonth() + 1 && !cal?.archived
  );
  const stampsThisMonth = currentCalendar?.totalStamps || 0;
  const hasTopWeekStatus = currentCalendar && (currentCalendar.totalStamps || 0) >= 7;

  // Calculate Daily Streak (locale, same logic as Dashboard)
  const today = new Date().toISOString().split('T')[0];
  const calculateDailyStreak = (): { streak: number; isActive: boolean; lastActiveDate: string | null } => {
    const lastDate = profileUser?.dailyCoreActionDate || null;
    if (!lastDate) {
      return { streak: 0, isActive: false, lastActiveDate: null };
    }

    const lastDateObj = new Date(lastDate);
    const todayObj = new Date(today);
    const yesterdayObj = new Date(todayObj);
    yesterdayObj.setDate(yesterdayObj.getDate() - 1);

    const isToday = lastDate === today;
    const isYesterday = lastDate === yesterdayObj.toISOString().split('T')[0];
    const isActive = isToday || isYesterday;

    if (!isActive) {
      return { streak: 0, isActive: false, lastActiveDate: lastDate };
    }

    // Try to get streak from localStorage if available
    const storedStreak = localStorage.getItem(`dailyStreak_${userId}`);
    if (storedStreak) {
      try {
        const data = JSON.parse(storedStreak);
        if (data.lastDate === today) {
          return { streak: data.streak || 1, isActive: true, lastActiveDate: lastDate };
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    return { streak: 1, isActive, lastActiveDate: lastDate };
  };

  const dailyStreak = calculateDailyStreak();

  // Get "Ultima Traccia" (last trace)
  const getUltimaTraccia = (): { type: 'action' | 'post' | 'active' | 'silent'; message: string } => {
    const lastPost = userPosts.length > 0 ? userPosts[0] : null;
    const lastActionDate = profileUser?.dailyCoreActionDate;
    const isActiveToday = lastActionDate === today;

    if (isActiveToday) {
      return { type: 'active', message: 'Attivo oggi' };
    }

    if (lastPost) {
      const postDate = lastPost.timestamp?.toDate ? lastPost.timestamp.toDate() : new Date(lastPost.timestamp);
      const daysAgo = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysAgo <= 7) {
        return { type: 'post', message: `Ultimo post ${daysAgo === 0 ? 'oggi' : `${daysAgo} giorni fa`}` };
      }
    }

    if (lastActionDate) {
      const actionDate = new Date(lastActionDate);
      const daysAgo = Math.floor((now.getTime() - actionDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysAgo <= 7) {
        return { type: 'action', message: `Ultima azione ${daysAgo === 0 ? 'oggi' : `${daysAgo} giorni fa`}` };
      }
    }

    return { type: 'silent', message: 'Sta costruendo in silenzio.' };
  };

  const ultimaTraccia = getUltimaTraccia();

const displayedFollowersCount =
  auth.currentUser &&
  userId &&
  typeof userId === 'string' &&
  userId.trim() !== ''
    ? activeFollowersCount
    : 0;


  
  // Safe defaults for all profile fields
  // CRITICAL: profileUser is guaranteed to exist at this point (checked above), but use optional chaining for safety
  const safeProfileUser: User = {
    ...profileUser,
    id: profileUser?.id || userId,
    name: profileUser?.name || t('profile.user_default') || 'User',
    email: profileUser?.email || '',
    avatar: profileUser?.avatar || profileUser?.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`,
    photoURL: profileUser?.photoURL || profileUser?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`,
    level: profileUser?.level || 1,
    xp: profileUser?.xp || 0,
    badges: profileUser?.badges || [],
    bio: profileUser?.bio || '',
    username: profileUser?.username || undefined,
    title: profileUser?.title || undefined,
    role: profileUser?.role || 'user',
    plan: profileUser?.plan || 'free',
    monthlyCalendars: profileUser?.monthlyCalendars || []
  };

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-4xl mx-auto animate-fadeIn">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-white mb-4">
        <ArrowLeft size={20} /> {t('profile.back')}
      </button>

      {/* Profile Header */}
      <div className="relative bg-gradient-to-br from-slate-900 to-slate-950 rounded-[3rem] p-8 border border-slate-800 shadow-2xl overflow-hidden">
        <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
          <div className="relative group">
            <div className={`w-32 h-32 rounded-full p-1.5 bg-gradient-to-tr ${
              profileUser.level >= 50 ? 'from-cyan-400 to-emerald-500' : 
              profileUser.level >= 30 ? 'from-yellow-400 to-orange-500' : 
              'from-slate-700 to-slate-500'
            }`}>
              <img 
                src={avatarPreview || safeProfileUser.photoURL || safeProfileUser.avatar} 
                className="w-full h-full rounded-full border-4 border-slate-900 object-cover" 
                alt={safeProfileUser.name}
                onError={(e) => {
                  // Fallback to default avatar if image fails to load
                  (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`;
                }}
              />
            </div>
            {isOwnProfile && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={uploadingAvatar}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute bottom-0 right-0 p-3 bg-emerald-500 rounded-full text-white hover:bg-emerald-600 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center"
                    title={t('profile.change_photo')}
                >
                  {uploadingAvatar ? (
                    <Loader className="animate-spin" size={16} />
                  ) : (
                    <Camera size={16} />
                  )}
                </button>
              </>
            )}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-white font-black text-sm px-4 py-1 rounded-full shadow-lg">
              {t('profile.level_short')} {profileUser.level}
            </div>
          </div>

          <div className="flex-1 text-center md:text-left space-y-4">
            <div>
              <h1 className="text-3xl font-black mb-1 flex items-center justify-center md:justify-start gap-3">
                {safeProfileUser.name} 
                {safeProfileUser.level >= 50 && <Award className="text-emerald-400" />}
              </h1>
              {safeProfileUser.username && (
                <p className="text-cyan-400 font-bold text-sm mb-1">@{safeProfileUser.username}</p>
              )}
              <p className="text-emerald-500 font-black text-xs uppercase tracking-widest">{safeProfileUser.title || t('profile.title_default')}</p>
              {/* Social Status Labels */}
              <div className="flex flex-wrap gap-2 mt-2">
                {safeProfileUser.level >= 30 && (
                  <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest bg-cyan-500/10 px-2 py-1 rounded-full border border-cyan-500/20">
                    {t('social_status.discipline_high')}
                  </span>
                )}
                {hasTopWeekStatus && (
                  <span className="text-[9px] font-black text-orange-400 uppercase tracking-widest bg-orange-500/10 px-2 py-1 rounded-full border border-orange-500/20">
                    {t('social_status.top_week', { percent: '10' })}
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons (Follow/Unfollow, Message) */}
            {!isOwnProfile && currentUser?.id && (
              <div className="flex items-center gap-3 justify-center md:justify-start">
                <button
                  onClick={async () => {
                    if (!currentUser?.id) return;
                    try {
                      if (isFollowingUser) {
                        await unfollowUser(currentUser.id, userId);
                        setIsFollowingUser(false);
                        setIsMutual(false);
                      } else {
                        await followUser(currentUser.id, userId);
                        setIsFollowingUser(true);
                        // Check if mutual after follow
                        const mutual = await isMutualFollow(currentUser.id, userId);
                        setIsMutual(mutual);
                      }
                    } catch (error: any) {
                      if (import.meta.env.DEV) {
                        console.error('[PROFILE] Error toggling follow:', error);
                      }
                      alert(error?.message || t('profile.follow_error') || 'Errore durante l\'operazione');
                    }
                  }}
                  className={`px-6 py-3 rounded-xl font-black text-sm uppercase tracking-wider transition-all flex items-center gap-2 ${
                    isFollowingUser
                      ? 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                      : 'bg-cyan-500 text-white hover:bg-cyan-600'
                  }`}
                >
                  {isFollowingUser ? (
                    <>
                      <CheckCircle size={18} />
                      <span>{t('profile.unfollow') || 'Unfollow'}</span>
                    </>
                  ) : (
                    <>
                      <UserIcon size={18} />
                      <span>{t('profile.follow') || 'Follow'}</span>
                    </>
                  )}
                </button>
                
                {isMutual && (
                  <div className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <span className="text-xs font-black text-emerald-400 uppercase tracking-wider">
                      {t('profile.mutual_follow') || 'Mutual Follow'}
                    </span>
                  </div>
                )}

                {onNavigateToChat && (
                  <button
                    onClick={async () => {
                      try {
                        if (!currentUser?.id) return;
                        const conversationId = await getOrCreateConversation(currentUser.id, userId);
                        onNavigateToChat(conversationId, userId);
                      } catch (error: any) {
                        if (error?.message?.includes('yourself')) {
                          alert(t('profile.cannot_message_yourself') || 'Non puoi inviare messaggi a te stesso');
                        } else {
                          alert(error?.message || t('profile.message_error') || 'Errore durante la creazione della conversazione');
                        }
                      }
                    }}
                    className="px-6 py-3 bg-slate-800 text-slate-300 hover:bg-slate-700 rounded-xl font-black text-sm uppercase tracking-wider transition-all flex items-center gap-2 border border-slate-700"
                  >
                    <MessageSquare size={18} />
                    <span>{t('profile.message') || 'Messaggio'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Bio Section */}
            <div className="w-full">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">{t('profile.bio')}</h3>
                {isOwnProfile && !isEditingBio && (
                  <button
                    onClick={() => setIsEditingBio(true)}
                    className="p-1.5 text-slate-500 hover:text-emerald-400 transition-colors rounded-lg hover:bg-slate-800"
                    title={t('profile.edit_bio')}
                  >
                    <Edit2 size={14} />
                  </button>
                )}
              </div>
              {isEditingBio && isOwnProfile ? (
                <div className="space-y-2">
                  <textarea
                    value={bioText}
                    onChange={(e) => setBioText(e.target.value)}
                    placeholder={t('profile.bio_placeholder')}
                    maxLength={200}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-sm text-white placeholder-slate-500 resize-none outline-none focus:border-emerald-500"
                    rows={3}
                  />
                  <div className="flex items-center gap-2 justify-center md:justify-start">
                    <button
                      onClick={async () => {
                        await updateProfile(userId, { bio: bioText.trim() });
                        setIsEditingBio(false);
                      }}
                      className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-600 transition-all"
                    >
                      {t('profile.save')}
                    </button>
                    <button
                      onClick={() => {
                        setBioText(profileUser.bio || '');
                        setIsEditingBio(false);
                      }}
                      className="px-4 py-2 bg-slate-800 text-slate-400 rounded-xl text-xs font-black uppercase hover:bg-slate-700 transition-all"
                    >
                      {t('profile.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                  {canSeePrivateInfo ? (
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {safeProfileUser.bio || (isOwnProfile ? t('profile.bio_empty_own') : t('profile.bio_empty_other'))}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500 italic">
                      {t('profile.bio_private') || 'Bio privata - Segui l\'utente per vedere di più'}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <LevelProgress xp={safeProfileUser.xp} level={safeProfileUser.level} />
            
            {/* Momentum Section */}
            <div className="bg-gradient-to-r from-orange-500/10 to-emerald-500/10 border border-orange-500/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="text-orange-400" size={18} />
                  <span className="text-xs font-black uppercase tracking-widest text-slate-300">Momentum</span>
                </div>
                {dailyStreak.isActive && dailyStreak.streak > 0 && (
                  <div className="flex items-center gap-1">
                    <Flame className="text-orange-400" size={16} fill="currentColor" />
                    <span className="text-sm font-black text-orange-400">{dailyStreak.streak}</span>
                  </div>
                )}
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-orange-500 to-emerald-500 transition-all"
                  style={{ width: `${Math.min((dailyStreak.streak / 7) * 100, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-2 italic">{ultimaTraccia.message}</p>
            </div>
            
            <div className="flex flex-wrap justify-center md:justify-start gap-4 items-center">
              <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-2xl border border-emerald-500/20">
                <CheckCircle size={18} fill="currentColor" />
                <span className="font-black text-lg">{stampsThisMonth} {t('calendar.stamps_this_month_short')}</span>
              </div>
              <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-500 px-4 py-2 rounded-2xl border border-emerald-500/20">
                <Trophy size={18} />
                <span className="font-black text-lg">{safeProfileUser.xp.toLocaleString()} {t('profile.xp')}</span>
              </div>
              {!isOwnProfile && onNavigateToChat && currentUser?.id && (
                <button
                  onClick={async () => {
                    try {
                      // CRITICAL: Safe access to currentUser.id
                      if (!currentUser?.id) {
                        alert(t('chat.error.auth_required') || 'Autenticazione richiesta');
                        return;
                      }
                      const conversationId = await getOrCreateConversation(currentUser.id, userId);
                      onNavigateToChat(conversationId, userId);
                    } catch (error: any) {
                      if (error?.message?.includes('yourself')) {
                        alert(t('chat.error.self_message'));
                      } else {
                        console.error('[PROFILE] Error creating conversation:', error);
                        alert(t('chat.error.conversation') || 'Errore nella creazione della conversazione');
                      }
                    }
                  }}
                  className="flex items-center gap-2 bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-2xl border border-cyan-500/20 hover:bg-cyan-500/20 transition-colors"
                >
                  <MessageSquare size={18} />
                  <span className="font-black text-sm uppercase tracking-wider">{t('profile.message')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] text-center">
          <p className="text-2xl font-black text-emerald-400">{safeProfileUser.level}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-2">{t('profile.level')}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] text-center">
          <p className="text-2xl font-black text-emerald-400">{stampsThisMonth}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-2">{t('calendar.stamps_this_month_short')}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] text-center">
          <p className="text-2xl font-black text-cyan-400">{safeProfileUser.xp.toLocaleString()}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-2">{t('profile.xp')}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] text-center">
          <p className="text-2xl font-black text-blue-400">{safeProfileUser.badges.length}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-2">{t('profile.badges')}</p>
        </div>
      </div>

      {/* Badges Section */}
      {safeProfileUser.badges && safeProfileUser.badges.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8">
          <h2 className="text-xl font-black mb-6 flex items-center gap-3 uppercase italic tracking-tighter">
            <Award className="text-emerald-400" /> {t('profile.badges')}
          </h2>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
            {safeProfileUser.badges.map((badgeId, index) => (
              <div key={index} className="aspect-square bg-slate-800 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-3xl">
                🏆
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Posts Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8">
        <h2 className="text-xl font-black mb-6 flex items-center gap-3 uppercase italic tracking-tighter">
          <ImageIcon className="text-emerald-400" /> {t('profile.posts_published')}
        </h2>
        {postsError ? (
          <div className="text-center py-12">
            <p className="text-slate-500 text-sm">
              {t('profile.posts_error') || 'Contenuti non disponibili'}
            </p>
          </div>
        ) : userPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500 text-sm">
              {isOwnProfile ? t('profile.no_posts_own') : t('profile.no_posts_other')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {userPosts.map(post => (
              <div key={post?.id || Math.random()} className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
                {post?.imageUrl && (
                  <div className="aspect-video bg-slate-900 overflow-hidden">
                    <img 
                      src={post.imageUrl} 
                      alt="Post" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Hide broken images
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {post?.videoUrl && (
                  <div className="aspect-video bg-slate-900 overflow-hidden">
                    <video src={post.videoUrl} controls className="w-full h-full object-cover" />
                  </div>
                )}
                {post?.text && (
                  <div className="p-4">
                    <p className="text-sm text-slate-300 line-clamp-2">{post.text}</p>
                  </div>
                )}
                <div className="px-4 pb-4 flex items-center gap-4 text-xs text-slate-500">
                  <span>❤️ {post?.likes || 0}</span>
                  <span>💬 {post?.commentsCount || 0}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile Gravity */}
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8">
        <h2 className="text-xl font-black mb-4 flex items-center gap-3 uppercase italic tracking-tighter">
          <UserIcon className="text-cyan-400" /> Profile Gravity
        </h2>
        {activeFollowersCount > 0 ? (
          <div className="text-center py-8">
            <p className="text-2xl font-black text-cyan-400 mb-2">{activeFollowersCount}</p>
            <p className="text-sm text-slate-400">
              Seguito da persone che agiscono ogni giorno
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">
              Non è vanity. È segnale di qualità.
            </p>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-lg font-bold text-slate-400 mb-2">Tra i primi a muoversi.</p>
            <p className="text-sm text-slate-500 italic">
              Il tuo profilo diventerà magnetico con la costanza.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
